import type { PlayerSide } from '../types.js';
import { getConfig } from '../config.js';

export interface CoinFlipPortrait {
  /** Display name for the result banner ("X goes first") and the
   *  caption strip under the portrait. */
  name: string;
  /** Path to the card art image (full art, not the 1:1 avatar crop). */
  art: string;
}

/** Mode of the opening ceremony.
 *
 *  - `flip`: a spinning coin lands on the result. Use when the rule's
 *    `startingPlayer` is `null` and turn order really is decided here.
 *  - `announce`: skip the coin spin entirely; the result banner shows
 *    immediately after the portrait slide-in. Use when the rule
 *    pre-determines who goes first — playtesters found the spinning
 *    coin misleading there because it implied randomness that didn't
 *    exist. The portrait reveal still plays so campaign stages keep
 *    their fighting-game-intro feel. */
export type CoinFlipMode = 'flip' | 'announce';

/** Fighting-game-style pre-battle ceremony.
 *
 *  Timeline:
 *    1. Both portraits slide on screen — player from the left, opponent
 *       from the right. Player sits a hair lower, opponent a hair
 *       higher, so they read as a pair with depth rather than mirror
 *       symmetry. Portrait mode rotates to lower-left / upper-right.
 *    2. In `flip` mode, the coin starts spinning as the slide begins.
 *       We wait for both portraits' art to finish loading (or the
 *       `coinFlipArtTimeoutMs` deadline) before settling.
 *    3. Coin stops, the result banner shows "You go first" or
 *       "{Opponent name} goes first," and the starting player's
 *       portrait gets a glowing-border highlight.
 *    4. After a short read window the overlay fades and `onComplete`
 *       fires.
 *
 *  Either portrait can be null — falls back to a name-only block on
 *  that side. Both null is functionally identical to the original
 *  result-only ceremony. */
export function showCoinFlip(
  firstPlayer: PlayerSide,
  playerPortrait: CoinFlipPortrait | null,
  opponentPortrait: CoinFlipPortrait | null,
  mode: CoinFlipMode,
  onComplete: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = `coin-flip-overlay ${mode === 'announce' ? 'announce' : ''}`;
  overlay.innerHTML = `
    <div class="coin-flip-stage">
      ${mode === 'flip' ? '<div class="coin-flip-coin"></div>' : ''}
      <div class="coin-flip-text">${mode === 'flip' ? 'Coin flip…' : ''}</div>
    </div>
    ${renderPortraitPanel('player', playerPortrait)}
    ${renderPortraitPanel('opponent', opponentPortrait)}
  `;
  document.body.appendChild(overlay);

  const text = overlay.querySelector('.coin-flip-text') as HTMLElement;
  const opponentLabel = opponentPortrait?.name ?? 'Opponent';
  const resultText = firstPlayer === 'player' ? 'You go first' : `${opponentLabel} goes first`;

  // Wait for portrait art to finish loading (or the timeout to expire)
  // before settling. The coin keeps spinning during the wait; in
  // announce mode there's no coin so the wait is purely silent.
  const waits: Promise<void>[] = [];
  if (playerPortrait) waits.push(waitForImage(playerPortrait.art, getConfig().coinFlipArtTimeoutMs));
  if (opponentPortrait) waits.push(waitForImage(opponentPortrait.art, getConfig().coinFlipArtTimeoutMs));
  const ready = Promise.all(waits);

  ready.then(() => {
    if (mode === 'flip') {
      // Settle the coin after a short post-load beat so the spin
      // doesn't cut off the instant the images arrive.
      setTimeout(() => {
        text.textContent = resultText;
        overlay.classList.add('settled');
        overlay.classList.add(`first-${firstPlayer}`);
      }, 700);

      setTimeout(() => {
        overlay.classList.add('fade-out');
        setTimeout(() => { overlay.remove(); onComplete(); }, 300);
      }, 2300);
    } else {
      // Announce: result is up immediately after the slide-in lands.
      // Trim the on-screen time accordingly.
      text.textContent = resultText;
      overlay.classList.add('settled');
      overlay.classList.add(`first-${firstPlayer}`);
      setTimeout(() => {
        overlay.classList.add('fade-out');
        setTimeout(() => { overlay.remove(); onComplete(); }, 300);
      }, 1700);
    }
  });
}

function renderPortraitPanel(side: 'player' | 'opponent', portrait: CoinFlipPortrait | null): string {
  if (!portrait) return '';
  // `data-side` drives the position + slide direction in CSS; both
  // sides share the same `.coin-flip-portrait` shell so the
  // animation, border, and caption strip stay in sync.
  return `
    <div class="coin-flip-portrait" data-side="${side}">
      <img src="${escapeAttr(portrait.art)}" alt="${escapeAttr(portrait.name)}" draggable="false" onerror="this.style.display='none'" />
      <div class="coin-flip-portrait-name">${escapeHtml(portrait.name)}</div>
    </div>
  `;
}

/** Resolve as soon as the image has loaded (or errored), or when
 *  `timeoutMs` elapses — whichever comes first. The promise is
 *  intentionally fire-and-forget: we never reject. The ceremony
 *  treats both outcomes the same (start the settle animation
 *  regardless; the panel's `onerror` hides the image element if
 *  the file genuinely doesn't exist). */
function waitForImage(src: string, timeoutMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    const img = new Image();
    img.onload = finish;
    img.onerror = finish;
    img.src = src;
    // Synchronous fast-path: if the browser has the image cached and it's
    // already decoded, `complete` is true immediately after src assignment.
    if (img.complete) finish();
    setTimeout(finish, timeoutMs);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}
